import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { IntegrationCard } from '@/components/integrations/IntegrationCard'
import {
  integrationsClient,
  IntegrationStatus,
} from '@/lib/api/integrations/integrations.client'
import {
  VISIBLE_INTEGRATIONS,
  INTEGRATIONS,
  INTEGRATION_CALLBACK_PARAMS,
  getIntegrationSyncConfig,
  type IntegrationSyncType,
} from '@super/shared-types'
import { toast } from 'sonner'
import { ROUTE_REGISTRY, KNOWLEDGE_TAB_PARAMS } from '@/lib/routes'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAppAuth } from '@/contexts/app-auth.context'
import { SyncTypeConfigPanel } from '@/components/integrations/SyncTypeConfigPanel'
import { dispatchCatalogClick } from '@/components/integrations/vendors'

const integrationClients = {
  APPFOLIO: integrationsClient.appFolio,
  BUILDIUM: integrationsClient.buildium,
  HUBSPOT: integrationsClient.hubspot,
  BUILDING_LINK: integrationsClient.buildingLink,
  EPIC_PM: integrationsClient.epicPm,
  PROPERTY_MELD: integrationsClient.propertyMeld,
  RENT_MANAGER: integrationsClient.rentManager,
  RENTVINE: integrationsClient.rentvine,
  DOORLOOP: integrationsClient.doorLoop,
  SHOWMOJO: integrationsClient.showMojo,
  TENANT_TURNER: integrationsClient.tenantTurner,
  YARDI_VOYAGER: integrationsClient.yardiVoyager,
  YARDI_BREEZE: integrationsClient.yardiBreeze,
  ENTRATA: integrationsClient.entrata,
}

export function IntegrationsSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [integrationStatuses, setIntegrationStatuses] = useState<
    IntegrationStatus[]
  >([])
  const [loading, setLoading] = useState(true)
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false)
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState<
    (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS] | null
  >(null)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [syncingIntegrationId, setSyncingIntegrationId] = useState<
    string | null
  >(null)
  const [syncConfigIntegration, setSyncConfigIntegration] = useState<
    (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS] | null
  >(null)
  const [disabledSyncTypesByIntegration, setDisabledSyncTypesByIntegration] =
    useState<Record<string, IntegrationSyncType[]>>({})

  // Load integration statuses on mount and when currentCompany or admin status changes
  useEffect(() => {
    if (currentCompany?.id) {
      loadIntegrationStatuses()
    }
  }, [currentCompany?.id])

  const handleIntegrationSuccess = (successParam: string) => {
    setTimeout(() => {
      if (
        successParam === INTEGRATION_CALLBACK_PARAMS.SUCCESS.BUILDIUM_CONNECTED
      ) {
        toast.success('Buildium integration connected successfully!', {
          description: (
            <div className="flex flex-col gap-1">
              <p>We are syncing your listings now.</p>
              <a
                href={`${ROUTE_REGISTRY.KNOWLEDGE.path}?tab=${KNOWLEDGE_TAB_PARAMS.LISTINGS}`}
                className="text-xs underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                View listings
              </a>
            </div>
          ),
        })
      } else if (
        successParam ===
        INTEGRATION_CALLBACK_PARAMS.SUCCESS.PROPERTY_MELD_CONNECTED
      ) {
        toast.success('PropertyMeld integration connected successfully!')
      } else if (
        successParam === INTEGRATION_CALLBACK_PARAMS.SUCCESS.HUBSPOT_CONNECTED
      ) {
        toast.success('HubSpot integration connected successfully!')
      } else {
        toast.success('Integration connected successfully!')
      }
    }, 100)
  }

  const handleIntegrationError = (errorParam: string) => {
    const errorMessages: Record<string, string> = {
      [INTEGRATION_CALLBACK_PARAMS.ERROR.NO_TOKEN]:
        'No authentication token received',
      [INTEGRATION_CALLBACK_PARAMS.ERROR.NO_STATE]:
        'Invalid authentication state',
      [INTEGRATION_CALLBACK_PARAMS.ERROR.CONNECTION_FAILED]:
        'Failed to connect integration',
    }

    setTimeout(() => {
      toast.error(errorMessages[errorParam] || 'Failed to connect integration')
    }, 100)
  }

  // Handle OAuth callback query parameters
  useEffect(() => {
    const success = searchParams.get('success')
    const error = searchParams.get('error')

    if (success) {
      handleIntegrationSuccess(success)
      setSearchParams({})
    } else if (error) {
      handleIntegrationError(error)
      setSearchParams({})
    }
  }, [searchParams])

  const loadIntegrationStatuses = async () => {
    if (!currentCompany?.id) return

    try {
      setLoading(true)
      const statuses = isSuperAdmin
        ? await integrationsClient.getAllAdminIntegrationsStatus(
            currentCompany.id,
          )
        : await integrationsClient.getAllIntegrationsStatus()
      setIntegrationStatuses(statuses)

      const disabledByType: Record<string, IntegrationSyncType[]> = {}
      for (const status of statuses) {
        if (status.disabledPullSyncTypes) {
          disabledByType[status.type] = status.disabledPullSyncTypes
        }
      }
      setDisabledSyncTypesByIntegration(disabledByType)
    } catch (error) {
      console.error('Error loading integration statuses:', error)
      toast.error('Failed to load integrations')
    } finally {
      setLoading(false)
    }
  }

  const handleConnectClick = async (
    integration: (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS],
  ) => {
    // Both catalogs — Settings + Onboarding — dispatch through the same
    // per-vendor registry. Vendor behavior (OAuth-direct vs form-sub-page,
    // pre-connect sync cleanup, admin vs non-admin variant) lives inside
    // each vendor's connector file.
    try {
      await dispatchCatalogClick(integration.TYPE, {
        mode: 'settings',
        navigate,
        isSuperAdmin,
        currentCompanyId: currentCompany?.id ?? null,
      })
    } catch (error) {
      console.error(`Error connecting ${integration.NAME}`, error)
      toast.error(`Couldn't start ${integration.NAME} connect.`)
    }
  }

  const handleEditClick = (
    integration: (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS],
  ) => {
    // Navigate to the integration setup page with edit mode
    if ('URL' in integration && integration.URL) {
      navigate(
        `${ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path}/${integration.URL}?mode=edit`,
      )
    }
  }

  const handleDisconnectClick = (
    integration: (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS],
  ) => {
    setIntegrationToDisconnect(integration)
    setDisconnectDialogOpen(true)
  }

  const confirmDisconnect = async () => {
    if (!integrationToDisconnect || !currentCompany?.id) return

    const client =
      integrationClients[
        integrationToDisconnect.TYPE as keyof typeof integrationClients
      ]

    if (!client) {
      toast.error('Disconnect not yet implemented for this integration')
      return
    }

    try {
      setIsDisconnecting(true)

      if (isSuperAdmin) {
        await client.disconnectAdmin(currentCompany.id)
      } else {
        await client.disconnect()
      }

      toast.success(
        `${integrationToDisconnect.NAME} integration disconnected successfully`,
      )

      // Reload integration statuses
      await loadIntegrationStatuses()
    } catch (error) {
      console.error('Error disconnecting integration:', error)
      toast.error('Failed to disconnect integration')
    } finally {
      setIsDisconnecting(false)
      setDisconnectDialogOpen(false)
      setIntegrationToDisconnect(null)
    }
  }

  const cancelDisconnect = () => {
    setDisconnectDialogOpen(false)
    setIntegrationToDisconnect(null)
  }

  const handleSyncClick = async (
    integration: (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS],
  ) => {
    const status = getStatusForIntegration(integration.TYPE)

    if (!status?.connected || !status?.id) {
      toast.error('Cannot sync: integration not connected')
      return
    }

    try {
      setSyncingIntegrationId(status.id)
      const jobs = isSuperAdmin
        ? await integrationsClient.triggerSyncAdmin(status.id)
        : await integrationsClient.triggerSync(status.id)

      if (
        jobs.length > 0 &&
        jobs.every(job => job.status === 'skipped_disabled')
      ) {
        toast.warning(
          `Sync skipped for ${integration.NAME} — pull sync is disabled`,
        )
      } else {
        toast.success(`Sync started for ${integration.NAME}`)
      }

      // Wait a bit for the sync to process before refetching
      setTimeout(async () => {
        await loadIntegrationStatuses()
        setSyncingIntegrationId(null)
      }, 2000)
    } catch (error) {
      console.error('Error triggering sync:', error)
      toast.error(`Failed to start sync for ${integration.NAME}`)
      setSyncingIntegrationId(null)
    }
  }

  const getStatusForIntegration = (integrationType: string) => {
    return integrationStatuses.find(s => s.type === integrationType)
  }

  const handleConfigureSyncClick = (
    integration: (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS],
  ) => {
    setSyncConfigIntegration(integration)
  }

  if (loading) {
    return (
      <div className="h-[calc(100%-4rem)] overflow-y-auto mt-[4rem]">
        <div className="flex flex-col gap-8 px-6 mt-6">
          <PageHeader title="Connect and manage third-party services and tools" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="h-32 bg-secondary/50 animate-pulse rounded-lg"
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="h-full mt-16">
        <div className="flex flex-col gap-8 px-6 pb-16 mt-6">
          <PageHeader title="Connect and manage third-party services and tools" />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {VISIBLE_INTEGRATIONS.map(integration => {
              const status = getStatusForIntegration(integration.TYPE)
              return (
                <IntegrationCard
                  key={integration.TYPE}
                  integration={integration}
                  status={status}
                  onConnect={() => handleConnectClick(integration)}
                  onDisconnect={() => handleDisconnectClick(integration)}
                  onEdit={() => handleEditClick(integration)}
                  onSync={() => handleSyncClick(integration)}
                  onConfigureSync={() => handleConfigureSyncClick(integration)}
                  disabledSyncTypes={
                    disabledSyncTypesByIntegration[integration.TYPE] ?? []
                  }
                  syncStatusLoading={loading}
                  isSyncing={status?.id === syncingIntegrationId}
                />
              )
            })}
          </div>
        </div>
      </div>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onOpenChange={setDisconnectDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Disconnect {integrationToDisconnect?.NAME}?
            </DialogTitle>
            <DialogDescription>
              This will remove the integration and delete all stored
              credentials. You'll need to reconnect if you want to use this
              integration again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelDisconnect}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Config Dialog — settable before or after the integration is connected */}
      <Dialog
        open={syncConfigIntegration !== null}
        onOpenChange={open => !open && setSyncConfigIntegration(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Enable {syncConfigIntegration?.NAME} synchronization
            </DialogTitle>
            <DialogDescription>
              Select the {syncConfigIntegration?.NAME} resources you want to
              sync into your Super account. We will automatically refresh and
              sync in the background for you every{' '}
              {syncConfigIntegration &&
                Math.round(
                  (getIntegrationSyncConfig(syncConfigIntegration.TYPE)
                    ?.defaultInterval ?? 0) /
                    (60 * 60 * 1000),
                )}{' '}
              hours.
            </DialogDescription>
          </DialogHeader>
          {syncConfigIntegration && (
            <SyncTypeConfigPanel
              integrationType={syncConfigIntegration.TYPE}
              syncTypes={
                getIntegrationSyncConfig(syncConfigIntegration.TYPE)
                  ?.syncTypes ?? []
              }
              disabledSyncTypes={
                disabledSyncTypesByIntegration[syncConfigIntegration.TYPE] ?? []
              }
              onDisabledSyncTypesChange={disabledSyncTypes =>
                setDisabledSyncTypesByIntegration(prev => ({
                  ...prev,
                  [syncConfigIntegration.TYPE]: disabledSyncTypes,
                }))
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
